import { apiClient } from './api';

type BoOrderStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface OrderItem {
  id: string;
  productName: string;
  sku: string;
  weight?: number;
  unitPrice: number;
  quantity: number;
  subtotal: number;
}

export interface Order {
  id: string;
  orderId: string;
  tenantId: string;
  tenantName: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  items: OrderItem[];
  itemCount: number;
  total: number;
  status: BoOrderStatus;
  paymentMethod: string;
  paymentStatus: string;
  transactionId?: string;
  paidAmount?: number;
  change?: number;
  notes?: string;
  timeline?: Array<{
    status: string;
    timestamp: string;
    note?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface OrderFilters {
  tenantId?: string;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface OrdersResponse {
  data: Order[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OrderStats {
  totalOrdersToday: number;
  revenueToday: number;
  pendingOrders: number;
  avgOrderValue: number;
}

const unwrapEnvelope = <T>(payload: any): T => {
  if (payload?.data !== undefined) {
    return payload.data as T;
  }
  return payload as T;
};

const normalizeOrderStatus = (status: string | undefined): BoOrderStatus => {
  if (status === 'canceled') return 'cancelled';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'completed') return 'completed';
  if (status === 'confirmed') return 'confirmed';
  return 'pending';
};

const normalizeOrder = (item: any): Order => ({
  id: item.id,
  orderId: item.orderId ?? item.order_id,
  tenantId: item.tenantId ?? item.tenant_id,
  tenantName: item.tenantName ?? item.tenant_name,
  customerName: item.customerName ?? item.customer_name,
  customerPhone: item.customerPhone ?? item.customer_phone,
  customerAddress: item.customerAddress ?? item.customer_address,
  items: Array.isArray(item.items) ? item.items : [],
  itemCount: item.itemCount ?? item.item_count ?? (Array.isArray(item.items) ? item.items.length : 0),
  total: item.total ?? 0,
  status: normalizeOrderStatus(item.status),
  paymentMethod: item.paymentMethod ?? item.payment_method ?? 'cash',
  paymentStatus: item.paymentStatus ?? item.payment_status ?? 'pending',
  transactionId: item.transactionId ?? item.transaction_id,
  paidAmount: item.paidAmount ?? item.paid_amount,
  change: item.change,
  notes: item.notes,
  timeline: Array.isArray(item.timeline) ? item.timeline : [],
  createdAt: item.createdAt ?? item.created_at,
  updatedAt: item.updatedAt ?? item.updated_at,
});

const normalizePaged = (payload: any): OrdersResponse => {
  const root = unwrapEnvelope<any>(payload);
  const items = root?.items ?? root?.data ?? [];
  const meta = root?.meta ?? {
    page: 1,
    limit: Array.isArray(items) ? items.length : 0,
    total: Array.isArray(items) ? items.length : 0,
    totalPages: 1,
  };

  return {
    data: (Array.isArray(items) ? items : []).map(normalizeOrder),
    meta,
  };
};

export const orderService = {
  getOrders: async (filters: OrderFilters = {}): Promise<OrdersResponse> => {
    const response = await apiClient.get('/orders', { params: filters });
    return normalizePaged(response.data);
  },

  getOrderById: async (id: string): Promise<Order> => {
    const response = await apiClient.get(`/orders/${id}`);
    return normalizeOrder(unwrapEnvelope<any>(response.data));
  },

  getOrderStats: async (): Promise<OrderStats> => {
    const response = await apiClient.get('/orders/stats');
    return unwrapEnvelope<OrderStats>(response.data);
  },

  cancelOrder: async (id: string, reason?: string): Promise<void> => {
    await apiClient.post(`/orders/${id}/cancel`, { reason });
  },
};

export default orderService;
